import { type PixelCanvas, sprite, TEAM_RECOLOR } from '../../lib';
import type { PaletteKey } from '../../palette';

/**
 * Team minions: blueberries (blue) and tomatoes (red, via recolor). Round body, leafy sprout, determined face, stubby
 * legs in boots. Drawn at 4× density (48×48 art px = 12×12 world px), feet on the origin row 47, authored facing right
 * (the lunge leans right). Procedural from a pose so every frame keeps the same construction.
 */
interface MinionPose {
  /** Body centre and radii (art px). */
  cx: number;
  cy: number;
  rx: number;
  ry: number;
  /** Shear: the top of the body sits `lean` px right of the bottom's mirror (+ = leaning forward). */
  lean: number;
  /** Feet x (left edge of each 6-px boot). */
  feet: readonly [number, number];
}

/** Top row of the boots' 2-px soles (the 2-px k outline goes under them, on the origin row). */
const SOLE = 44;

/** Squircle body (a touch squarer than an ellipse, like the old chunky berry); returns the normal's x/y, or null outside. */
const SQ = 2.5;
const inBody = (p: MinionPose, x: number, y: number): readonly [number, number] | null => {
  const ny = (y + 0.5 - p.cy) / p.ry;
  const nx = (x + 0.5 - p.cx + p.lean * ny) / p.rx;
  const d = Math.abs(nx) ** SQ + Math.abs(ny) ** SQ;
  if (d > 1) return null;
  const k = Math.min(1, Math.hypot(nx, ny)) / Math.max(1e-6, Math.hypot(nx, ny));
  return [nx * k * 0.95, ny * k * 0.95];
};

/** Top-centre of the body (where the crown or headgear sits): [x, y of the top body row]. */
const crownOf = (p: MinionPose): [number, number] => {
  const top = Math.ceil(p.cy - p.ry - 0.5);
  let y = top;
  while (y < p.cy && !inBody(p, Math.round(p.cx + p.lean), y)) y++;
  return [Math.round(p.cx + p.lean - 0.5), y];
};

const L = (() => {
  const [x, y, z] = [-0.45, -0.55, 0.7];
  const n = Math.hypot(x, y, z);
  return [x / n, y / n, z / n] as const;
})();

/**
 * The classic k outline at 4× density: 2 art px thick (the 1-px outline of the 2× art, doubled), round at the corners
 * (a pixel is outline within ~2 px of the shape).
 */
export function outline2(c: PixelCanvas, col: PaletteKey = 'k'): void {
  const solid = c.pixels.map((p) => p !== null);
  const at = (x: number, y: number) => x >= 0 && y >= 0 && x < c.w && y < c.h && solid[y * c.w + x];
  for (let y = 0; y < c.h; y++) {
    for (let x = 0; x < c.w; x++) {
      if (solid[y * c.w + x]) continue;
      let near = false;
      for (let dy = -2; dy <= 2 && !near; dy++)
        for (let dx = -2; dx <= 2 && !near; dx++) if (dx * dx + dy * dy <= 5 && at(x + dx, y + dy)) near = true;
      if (near) c.set(x, y, col);
    }
  }
}

/**
 * A leaf from its base (ax, ay) to its tip (tx, ty), `w` px at its widest: a lens lit on the side facing the top-left
 * (`lit` tone, `shade` on the far side) with a darker midrib (`vein`) from the base most of the way to the tip.
 */
export function leaf(
  c: PixelCanvas,
  [ax, ay]: readonly [number, number],
  [tx, ty]: readonly [number, number],
  w: number,
  lit: PaletteKey,
  shade: PaletteKey,
  vein: PaletteKey,
): void {
  const len = Math.hypot(tx - ax, ty - ay);
  const [ux, uy] = [(tx - ax) / len, (ty - ay) / len];
  // The side of the axis facing the light (top-left) is the lit half.
  const litSide = Math.sign(-uy * -0.45 + ux * -0.55) || 1;
  for (let y = Math.floor(Math.min(ay, ty) - w); y <= Math.ceil(Math.max(ay, ty) + w); y++) {
    for (let x = Math.floor(Math.min(ax, tx) - w); x <= Math.ceil(Math.max(ax, tx) + w); x++) {
      const [px, py] = [x + 0.5 - ax, y + 0.5 - ay];
      const t = (px * ux + py * uy) / len;
      if (t < 0 || t > 1) continue;
      const d = px * -uy + py * ux; // signed distance from the axis
      const half = (w / 2) * Math.sin(Math.PI * t ** 0.8) ** 0.8;
      if (Math.abs(d) > half) continue;
      const tone = Math.abs(d) < 0.55 && t > 0.08 && t < 0.78 ? vein : Math.sign(d) === litSide ? lit : shade;
      c.set(x, y, tone);
    }
  }
}

/**
 * Bead eye, 6×7 with a 2×2 w glint top-right (looking ahead) and a 5 speck of reflected light lower-left. Its brow is
 * drawn by `brow`.
 */
const EYE = `
  .kkkk.
  kkkkww
  kkkkww
  kkkkkk
  k5kkkk
  kkkkkk
  .kkkk.
`;

/** A brow: a 2-px bar slanting down towards the middle of the face (`dir` +1 for the left eye's, -1 for the right's). */
function brow(c: PixelCanvas, x0: number, y0: number, len: number, dir: 1 | -1): void {
  for (let i = 0; i < len; i++) {
    const x = x0 + i * dir;
    const y = y0 + Math.floor((i * 2) / len + 0.25);
    c.set(x, y, 'k').set(x, y + 1, 'k');
  }
}

/** Paints a minion (no outline) at vertical offset `oy`. `crown` false leaves the top bare (for helmets). */
function paintMinion(c: PixelCanvas, p: MinionPose, oy = 0, crown = true): void {
  // Legs + boots (toe forward = right): a lit n stripe, N behind; the boot's cap lit on its top-left.
  for (const fx of p.feet) {
    for (let y = Math.round(p.cy + p.ry - 4); y < SOLE; y++)
      c.rect(fx, y + oy, 2, 1, 'n').rect(fx + 2, y + oy, 2, 1, 'N');
    c.rect(fx, SOLE + oy, 6, 2, 'N');
    c.hline(fx, fx + 2, SOLE + oy, 'n').set(fx + 5, SOLE + oy, null);
    c.set(fx + 3, SOLE + oy, 'm');
  }
  // Body: lit volume (B shadow crescent, b, a c highlight top-left with a w glint), bounce light on the bottom rim.
  let glint: [number, number, number] = [-1, -1, -9];
  for (let y = Math.floor(p.cy - p.ry - 1); y <= Math.ceil(p.cy + p.ry + 1); y++) {
    for (let x = Math.floor(p.cx - p.rx - 2); x <= Math.ceil(p.cx + p.rx + 2); x++) {
      const hit = inBody(p, x, y);
      if (!hit) continue;
      const [nx, ny] = hit;
      const nz = Math.sqrt(Math.max(0, 1 - nx * nx - ny * ny));
      const dot = nx * L[0] + ny * L[1] + nz * L[2];
      let tone: PaletteKey = dot > 0.975 ? 'c' : dot > 0.02 ? 'b' : 'B';
      if (tone === 'B' && ny > 0.82 && nx < 0.5) tone = 'b';
      // A 1-px c rim light along the lit left edge.
      if (tone === 'b' && dot > 0.5 && nx < -0.55 && (!inBody(p, x - 1, y) || !inBody(p, x, y - 1))) tone = 'c';
      if (dot > glint[2]) glint = [x, y, dot];
      c.set(x, y + oy, tone);
    }
  }
  // The glint: three px of w at the top-left of the highlight.
  const [gx, gy] = glint;
  c.set(gx - 1, gy - 1 + oy, 'w')
    .set(gx, gy - 1 + oy, 'w')
    .set(gx - 1, gy + oy, 'w');
  // Face, right of centre: two bead eyes under brows slanting down towards the middle, a small set mouth.
  const ex = Math.round(p.cx + p.lean * 0.3);
  const ey = Math.round(p.cy - 3);
  c.stamp(EYE, ex - 8, ey + oy);
  c.stamp(EYE, ex + 4, ey + oy);
  brow(c, ex - 10, ey - 3 + oy, 6, 1);
  brow(c, ex + 11, ey - 3 + oy, 6, -1);
  c.hline(ex - 1, ex + 2, ey + 9 + oy, 'k').set(ex + 3, ey + 10 + oy, 'k');
  if (crown) {
    const [cx, cy] = crownOf(p);
    const y = cy + oy;
    // The sprout sits in the berry's crown dimple: a B dimple, a short stem, two leaves opening in a V, sepals on top.
    c.hline(cx - 2, cx + 3, y + 1, 'B').hline(cx - 1, cx + 2, y + 2, 'B');
    leaf(c, [cx + 0.5, y + 0.5], [cx - 5, y + 2.5], 2.6, 'l', 'g', 'g');
    leaf(c, [cx + 1.5, y + 0.5], [cx + 7, y + 2.5], 2.6, 'g', 'G', 'G');
    c.rect(cx, y - 3, 1, 4, 'l').rect(cx + 1, y - 3, 1, 4, 'G');
    leaf(c, [cx + 0.5, y - 2], [cx - 9, y - 6.5], 5.5, 'l', 'g', 'g');
    leaf(c, [cx + 1.5, y - 2], [cx + 10, y - 5.5], 5.5, 'g', 'G', 'G');
  }
}

const MINION_POSES = {
  /** Walk 1: up on its toes. */
  up: { cx: 24, cy: 24.8, rx: 18, ry: 14.8, lean: 0, feet: [14, 26] },
  /** Walk 2: landed, squashed two px. */
  down: { cx: 24, cy: 26.8, rx: 19, ry: 13.6, lean: 0, feet: [14, 26] },
  /** Wind-up: squashed down and back. */
  windup: { cx: 22, cy: 28.8, rx: 19.6, ry: 12.4, lean: -3, feet: [12, 24] },
  /** Lunge: stretched forward (right). */
  lunge: { cx: 26, cy: 24, rx: 17.2, ry: 15.2, lean: 6, feet: [15, 27] },
} as const satisfies Record<string, MinionPose>;

const frame = (p: MinionPose) => (c: PixelCanvas) => {
  paintMinion(c, p);
  outline2(c);
};

export default sprite({
  name: 'minion',
  group: 'actor',
  size: [48, 48],
  origin: [24, 47],
  density: 4,
  flash: true,
  recolor: TEAM_RECOLOR,
  notes: `Blueberry/tomato sproutling minion at 4× density (48×48 art px = 12×12 world px, the old footprint). Team colour
is the whole body so waves read at a glance: a squircle berry lit top-left (c highlight with a w glint, b, B shadow
crescent, b bounce light on the bottom rim), a sprout (l/g/G) in a B crown dimple so both teams stay "garden": two
leaves opening in a V with a midrib each, a stem, two sepals lying on the top; bead eyes with a w glint and a 5 speck under
2-px brows slanting down (soldiers, not pets), a small set mouth, n/N stub legs in boots toeing forward (a lit toe cap).
Classic k outline, 2 art px (the 2× art's 1 px): a B/G sel-out on the lit side was tried at 2× and softened the red
team's rim against the lawn (R is ~15 L* off g), so k.
walk 2f @8 (up on its toes → landed 2 px lower and wider), attack 3f @12 once (wind-up squashed back, lunge sheared 6 px
forward). Rubric: S4 R5 P5 L5 C4 A4 F4 (2×: S4 R5 P5 L5 C4 A4 F4).
Weakest: facing reads from the face and the lean, not the walk silhouette; the sprout's midribs merge with the leaves'
shaded halves; a little stair-banding along the shadow terminator.`,
  anims: {
    walk: { fps: 8, frames: [frame(MINION_POSES.up), frame(MINION_POSES.down)] },
    attack: {
      fps: 12,
      loop: false,
      frames: [frame(MINION_POSES.windup), frame(MINION_POSES.lunge), frame(MINION_POSES.lunge)],
    },
  },
});
